import React, { useState } from "react";
import { Card, Form, Button } from "react-bootstrap";
import { Filter, ChevronDown, ChevronUp } from "lucide-react";
import { FilterDefinition, ActiveFilter } from "../types";

interface FilterPanelProps {
  filterDefs: FilterDefinition[];
  activeFilters: ActiveFilter[];
  onFilterChange: (dimension: string, values: (number | string)[]) => void;
  onClearFilters: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  filterDefs,
  activeFilters,
  onFilterChange,
  onClearFilters,
}) => {
  const [expanded, setExpanded] = useState(false);

  if (filterDefs.length === 0) return null;

  const activeCount = activeFilters.filter((f) => f.values?.length > 0).length;

  const getSelectedValues = (dimension: string): string[] => {
    const filter = activeFilters.find((f) => f.dimension === dimension);
    return filter ? filter.values.map(String) : [];
  };

  const handleSelectChange = (
    dimension: string,
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selected = Array.from(e.target.selectedOptions, (opt) => {
      const numVal = Number(opt.value);
      return isNaN(numVal) ? opt.value : numVal;
    });
    onFilterChange(dimension, selected);
  };

  const clearOne = (dimension: string) => onFilterChange(dimension, []);

  return (
    <Card className="mb-3">
      <Card.Header
        onClick={() => setExpanded(!expanded)}
        className="gr-filter-header"
      >
        <span className="gr-filter-header-title">
          <Filter size={16} />
          Filters
          {activeCount > 0 && (
            <span className="grid-report-filter-chip">{activeCount}</span>
          )}
        </span>
        <span className="gr-filter-caret">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </Card.Header>
      {expanded && (
        <Card.Body style={{ padding: "18px 18px 14px" }}>
          <div className="gr-filter-grid">
            {filterDefs.map((fd) => {
              const selected = getSelectedValues(fd.key);
              return (
                <div key={fd.key} className="gr-filter-field">
                  <div className="gr-filter-field-label">
                    <span>{fd.label}</span>
                    {selected.length > 0 && (
                      <button
                        type="button"
                        className="gr-filter-field-clear"
                        onClick={() => clearOne(fd.key)}
                        title={`Clear ${fd.label}`}
                      >
                        {selected.length} selected &times;
                      </button>
                    )}
                  </div>
                  <Form.Select
                    multiple
                    className="gr-multiselect"
                    value={selected}
                    onChange={(e) => handleSelectChange(fd.key, e)}
                  >
                    {fd.options.map((opt) => (
                      <option key={opt.id} value={String(opt.id)}>
                        {opt.name}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              );
            })}
          </div>
          <div className="gr-filter-footer">
            <span className="gr-filter-hint">
              Hold <kbd>Ctrl</kbd> / <kbd>Cmd</kbd> to select multiple
            </span>
            {activeCount > 0 && (
              <Button variant="secondary" size="sm" onClick={onClearFilters}>
                Clear All Filters
              </Button>
            )}
          </div>
        </Card.Body>
      )}
    </Card>
  );
};

export default FilterPanel;
